import { useState, useEffect } from 'react';
import { type LookAndFeel, type LookAndFeelLoadingPage, type LookAndFeelRoot } from '@tectonics/look-and-feel';
import { handleError } from '@tectonics/observability';
import { loadModuleWithRetry } from '@tectonics/utils/modules';

type DynamicLookAndFeel = [boolean, LookAndFeelLoadingPage | null, LookAndFeelRoot | undefined];

const EmptyLoadingPage = () => null;
const defaultLookAndFeelLoadingPageState: () => LookAndFeelLoadingPage = () => EmptyLoadingPage;

const MODULE_LOAD_ERROR_MESSAGE = 'Dynamic Look And Feel - module failed to load';
const getModuleLoadError = () => new Error(MODULE_LOAD_ERROR_MESSAGE);

export function useDynamicLookAndFeel(lookAndFeel: LookAndFeel | null): DynamicLookAndFeel {
  const [loading, setLoading] = useState<boolean>(true);
  const [LafLoadingPage, setLafLoadingPage] = useState<LookAndFeelLoadingPage | null>(
    lookAndFeel && lookAndFeel.getLoadingPage ? defaultLookAndFeelLoadingPageState : null
  );
  const [LafMainPage, setLafMainPage] = useState<LookAndFeelRoot | undefined>(undefined);

  useEffect(() => {
    if (!lookAndFeel) return;

    let cancelled = false;

    (async () => {
      try {
        const getLoadingPage = lookAndFeel.getLoadingPage;

        if (getLoadingPage) {
          setLoading(true);
          const loadingPage = await loadModuleWithRetry(getLoadingPage, getModuleLoadError);
          if (cancelled) return;
          setLafLoadingPage((): LookAndFeelLoadingPage => loadingPage);
        }
      } catch (e) {
        handleError(e);
      }
    })();

    return () => { cancelled = true; };
  }, [lookAndFeel]);

  useEffect(() => {
    if (!lookAndFeel) return;

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const rootComponent = await loadModuleWithRetry(lookAndFeel.getRootComponent, getModuleLoadError);
        if (cancelled) return;
        setLafMainPage((): LookAndFeelRoot => rootComponent);
      } catch (e) {
        handleError(e);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [lookAndFeel]);

  return [loading, LafLoadingPage, LafMainPage];
}
